import { Box, Grid, Typography } from '@mui/material'
import BlogMailbox from 'public/images/illustration/blog-mailbox.svg'

import { Settings } from '@ecm/config/Settings'
import { Article } from '@ecm/data/Article'
import { Author } from '@ecm/data/Author'
import { runEff } from '@ecm/effect'
import NewsletterSignupCta from '@ecm/ui/component/block/NewsletterSignupCta'
import ArticleCard from '@ecm/ui/component/blog/ArticleCard'
import HeaderBlock from '@ecm/ui/component/blog/HeaderBlock'
import Breadcrumbs, { BreadCrumb } from '@ecm/ui/element/Breadcrumbs'
import Color from '@ecm/ui/element/Color'
import MainContainer from '@ecm/ui/element/MainContainer'
import Pagination from '@ecm/ui/element/Pagination'
import DefaultLayout from '@ecm/ui/layout/Default'

export type AuthorArticlesListProps = {
  author: Author
  authorId: string
  breadCrumb: BreadCrumb[]
  className?: string
  articles?: Article[]
  currentPage: number
  totalPages: number
}

export const AuthorArticlesList = ({
  author,
  authorId,
  currentPage,
  totalPages,
  breadCrumb,
  className,
  articles,
}: AuthorArticlesListProps) => {
  return (
    <DefaultLayout
      canonicalUrl={`blog/author/${authorId}${currentPage > 1 ? `/page/${currentPage}` : ''}`}
      finalCtaHeader="Need help with mail management? Join thousands of customers on Earth Class Mail"
      finalCtaSubHeader="Virtual Mail Plans Starting From $19 Per Month."
      finalCta="Sign up"
      seoTitle={`${author.fullname} - page ${currentPage}`}
      gradientBackground
    >
      <Box
        className={className}
        sx={{
          background: `linear-gradient(180deg, ${Color.smoke.normal} 250px, rgba(255, 255, 255, 0) 550px)`,
        }}
      >
        <MainContainer sx={{ paddingY: { md: '4em', xs: '2em' } }}>
          <Breadcrumbs breadCrumbList={breadCrumb} />
          <HeaderBlock author={author} illustration={BlogMailbox} />
          <Grid paddingBottom={{ md: 0, xs: '6em' }}>
            <Typography variant="h4" component="h2" color={Color.slate.normal}>
              Author articles
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gap: { md: '4em', xs: '2em' },
                gridTemplateColumns: {
                  lg: 'repeat(3, 1fr)',
                  sm: 'repeat(2, 1fr)',
                  xs: 'repeat(1, 1fr)',
                },
                gridTemplateRows: 'auto',
                justifyContent: 'space-between',
              }}
            >
              {articles &&
                articles.length > 0 &&
                articles.map((article, i) => {
                  return <ArticleCard key={article.id} article={article} priority={i < 3} />
                })}
            </Box>
            <Grid container justifyContent="center" paddingY="3.5em" width="100%">
              <Pagination
                currentPage={currentPage}
                pageSlug={`blog/author/${authorId}`}
                totalPages={totalPages}
              />
            </Grid>
          </Grid>
          <Grid paddingY="5em">
            <NewsletterSignupCta trackGaEvents />
          </Grid>
        </MainContainer>
      </Box>
    </DefaultLayout>
  )
}

export default AuthorArticlesList

export const getStaticProps = async (ctx: { params: { id: string } }) => {
  const authorId = ctx.params?.id

  const defaultAuthor = {
    fullname: 'Earth Class Mail Team',
  }

  const articlesByAuthor = await runEff(async eff => {
    if (!authorId) {
      return null
    }
    try {
      return await eff.getPaginatedArticlesById(authorId, 1)
    } catch (e) {
      return null
    }
  })
  if (!articlesByAuthor) {
    return {
      props: {
        articles: [],
        author: defaultAuthor,
        totalPages: 0,
      },
    }
  }

  const breadCrumb: BreadCrumb[] = [
    { description: 'Blog', href: '/blog', position: 2 },
    {
      description: articlesByAuthor.items.length
        ? articlesByAuthor.items[0].author.fullname
        : 'EarthClassMail',
      href: `/blog/${authorId}`,
      position: 3,
    },
  ]
  const totalPages = Math.ceil(articlesByAuthor.total / Settings.pagination.pageSize)

  return {
    props: {
      articles: articlesByAuthor.items,
      author: articlesByAuthor.items.length ? articlesByAuthor.items[0].author : defaultAuthor,
      authorId,
      breadCrumb,
      currentPage: 1,
      totalPages: totalPages,
    },
  }
}

export async function getStaticPaths() {
  const authors: Author[] = await runEff(eff => eff.getAuthors())
  const paths = authors.map(c => ({ params: { id: c.id } }))

  return {
    fallback: false,
    paths,
  }
}
